import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Image,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Drawer, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import useReview from "hooks/reviews/useReview";
import CopyableText from "components/common/CopyableText";
import paths from "routes/paths";

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const reviewStatusMap = {
  ACTIVE: "success",
  DELETED: "danger",
  DEACTIVATED: "warning",
  HIDE: "secondary",
};

const getDisplayName = (person = {}) => {
  const fromParts = [person.firstName, person.middleName, person.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    person.preferredFirstName ||
    fromParts ||
    person.nickName ||
    person.name ||
    "Anonymous"
  );
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const StarRating = ({ rating }) => {
  if (!rating) return <small className="text-muted">No rating</small>;
  const stars = Array.from({ length: 5 }, (_, i) =>
    i < Math.floor(rating) ? "★" : "☆",
  );
  return <span style={{ color: "#f4b400", fontSize: "0.95rem", letterSpacing: 1 }}>{stars.join("")}</span>;
};

const DrawerExpandButton = ({ fullWidth, onToggle }) => (
  <Button
    type="button"
    variant="light"
    size="sm"
    onClick={onToggle}
    title={fullWidth ? "Normal width" : "Expand drawer"}
    aria-label={fullWidth ? "Normal width" : "Expand drawer"}
    className="border rounded-pill d-inline-flex align-items-center justify-content-center"
    style={{
      width: 30,
      height: 30,
      padding: 0,
      lineHeight: 1,
      fontSize: 11,
    }}
  >
    <span style={{ fontWeight: 700 }}>{fullWidth ? "[]" : "[]"}</span>
  </Button>
);

const formatStatus = (status) =>
  status
    ? String(status)
        .replaceAll("_", " ")
        .toUpperCase()
    : "UNKNOWN";

const ReviewCard = ({ review, onDelete, onStatusChange }) => {
  const reviewerName = getDisplayName(review.reviewer);
  const initials = getInitials(reviewerName);
  const statusColor = reviewStatusMap[review.status] || "secondary";

  return (
    <Card
      className="mb-3 rounded-4 border-0 shadow-sm overflow-hidden"
      style={{
        borderLeft: `4px solid ${
          statusColor === "success"
            ? "#198754"
            : statusColor === "danger"
              ? "#dc3545"
              : statusColor === "warning"
                ? "#ffc107"
                : "#adb5bd"
        }`,
      }}
    >
      <Card.Body className="p-3">
        <Row className="align-items-start g-3">
          <Col xs="auto" className="d-flex align-items-center">
            {review.reviewer?.profilePhoto ? (
              <Image
                src={review.reviewer.profilePhoto}
                roundedCircle
                width={42}
                height={42}
                alt={reviewerName}
                onError={(e) => (e.target.style.display = "none")}
                style={{ objectFit: "cover" }}
                className="me-2"
              />
            ) : (
              <div
                className="bg-secondary text-white d-flex align-items-center justify-content-center me-2"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  fontWeight: 700,
                  fontSize: 15,
                  userSelect: "none",
                }}
              >
                {initials || "NA"}
              </div>
            )}

            <div>
              <div className="fw-semibold" style={{ fontSize: 13 }}>
                {reviewerName}
              </div>
              <div className="text-muted text-xxs" style={{ fontSize: 11 }}>
                {review.createdAt ? new Date(review.createdAt).toLocaleString() : "Unknown date"}
              </div>
            </div>
          </Col>

          <Col xs="auto" className="ms-auto d-flex flex-wrap align-items-center gap-2">
            <Badge bg={statusColor} className="text-uppercase" style={{ fontSize: 10, fontWeight: 700, padding: "0.3em 0.55em" }}>
              {review.status}
            </Badge>
            {review.source && (
              <Badge
                bg="light"
                text="dark"
                className="border text-uppercase"
                style={{ fontSize: 10, fontWeight: 700, padding: "0.3em 0.55em" }}
              >
                {review.source}
              </Badge>
            )}
          </Col>
        </Row>

        <div className="mt-3 d-flex align-items-center gap-2">
          <StarRating rating={review.rating} />
          {hasValue(review.rating) && (
            <span className="text-muted text-xxs" style={{ fontSize: 11 }}>
              {Number(review.rating).toFixed(1)}/5
            </span>
          )}
        </div>

        <p className="mt-2 mb-0 text-body-secondary" style={{ whiteSpace: "pre-line", fontSize: 13, lineHeight: 1.5 }}>
          {review.description || (
            <i className="text-muted fst-italic">No comment provided</i>
          )}
        </p>

        <div className="d-flex flex-wrap gap-2 mt-3 pt-1">
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-primary"
              size="sm"
              className="shadow-sm rounded-pill"
              style={{ fontSize: 11 }}
            >
              Status
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.keys(reviewStatusMap).map((status) => (
                <Dropdown.Item
                  key={status}
                  active={review.status === status}
                  onClick={() => onStatusChange(review.id, status)}
                >
                  {status}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            variant="outline-danger"
            size="sm"
            className="shadow-sm rounded-pill"
            style={{ fontSize: 11 }}
            onClick={() => onDelete(review.id)}
          >
            Delete
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const ReviewStats = ({ reviews = [] }) => {
  const total = reviews.length;
  const averageRating = total
    ? reviews.reduce((sum, review) => sum + (Number(review.rating) || 0), 0) / total
    : 0;
  const activeCount = reviews.filter((review) => review.status === "ACTIVE").length;
  const withSourceCount = reviews.filter((review) => hasValue(review.source)).length;

  const stats = [
    { label: "Total", value: total, tone: "primary" },
    { label: "Avg rating", value: total ? averageRating.toFixed(1) : "0.0", tone: "warning" },
    { label: "Active", value: activeCount, tone: "success" },
    { label: "Tagged", value: withSourceCount, tone: "secondary" },
  ];

  return (
    <Row className="g-2 mb-3">
      {stats.map((stat) => (
        <Col key={stat.label} xs={6} lg={3}>
          <Card
            className="border-0 rounded-4 shadow-sm h-100"
            style={{ background: "linear-gradient(180deg, #fff 0%, #f8fafc 100%)" }}
          >
            <Card.Body className="p-3">
              <div className="text-muted text-xxs text-uppercase fw-semibold" style={{ fontSize: 11 }}>
                {stat.label}
              </div>
              <div className="fw-semibold fs-4 lh-sm">{stat.value}</div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

function ReviewDrawerContent({ mode, taskId, helperUserId, requesterUserId, userName }) {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(5);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const reviewArgs =
    mode === "task"
      ? taskId
      : {
          ...(helperUserId ? { helperUserId } : {}),
          ...(requesterUserId ? { requesterUserId } : {}),
          page,
          size,
        };

  const {
    reviews = [],
    loading,
    error,
    totalElements,
    updateReviewStatus,
    deleteReview,
  } = useReview(reviewArgs);

  const handleDelete = (id) => setConfirmDeleteId(id);

  const confirmDelete = async () => {
    try {
      await deleteReview(confirmDeleteId, "SOFT");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleChangeStatus = async (id, status) => {
    try {
      await updateReviewStatus(id, status);
    } catch {
      // keep the drawer open and let the toast/error handling stay local to the hook callers
    }
  };

  const displayName = userName || (mode === "task" ? "Task" : "User");
  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
        <div>
          <h6 className="mb-1 fw-semibold" style={{ fontSize: 14 }}>
            {displayName} Reviews
          </h6>
          <div className="text-muted text-xxs" style={{ fontSize: 11 }}>
            Showing the latest review activity for this task
          </div>
        </div>
        <div className="text-muted text-xxs" style={{ fontSize: 11 }}>
          {reviews.length} loaded
        </div>
      </div>

      <ReviewStats reviews={reviews} />

      {loading && (
        <div className="d-flex align-items-center mb-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading reviews...
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mb-3">
          {error.message || "Failed to load reviews."}
        </Alert>
      )}

      {!loading && !error && reviews.length === 0 && (
        <Card className="border-0 rounded-4 shadow-sm bg-white">
          <Card.Body className="text-center py-4">
            <div className="text-muted fst-italic" style={{ fontSize: 13 }}>
              No reviews yet.
            </div>
          </Card.Body>
        </Card>
      )}

      {reviews.map((review) => (
        <ReviewCard
          key={review.id}
          review={review}
          onDelete={handleDelete}
          onStatusChange={handleChangeStatus}
        />
      ))}

      {mode !== "task" && (
        <div className="d-flex flex-wrap justify-content-between align-items-center mt-4 gap-2">
          <div>
            <Button
              variant="outline-primary"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="me-2"
            >
              &laquo; Previous
            </Button>
            <Button
              variant="outline-primary"
              disabled={(page + 1) * size >= totalElements}
              onClick={() => setPage(page + 1)}
            >
              Next &raquo;
            </Button>
          </div>

          <div className="d-flex align-items-center gap-2">
            <label htmlFor={`${mode}-review-page-size`} className="mb-0">
              Per page:
            </label>
            <select
              id={`${mode}-review-page-size`}
              value={size}
              onChange={(e) => {
                setSize(Number(e.target.value));
                setPage(0);
              }}
              className="form-select form-select-sm"
              style={{ width: 90 }}
            >
              {[5, 10, 20].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <Modal show={!!confirmDeleteId} onHide={() => setConfirmDeleteId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this review?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDeleteId(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export function TaskReviewsDrawer({ show, onHide, taskId, title = "Task Reviews" }) {
  const [fullWidth, setFullWidth] = useState(false);

  return (
    <Drawer
      open={show}
      onClose={onHide}
      placement="right"
      width={fullWidth ? "100vw" : 920}
      zIndex={2300}
      destroyOnClose
      title={<span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>}
      extra={
        <DrawerExpandButton
          fullWidth={fullWidth}
          onToggle={() => setFullWidth((value) => !value)}
        />
      }
      styles={{ body: { background: "#f8fafc", padding: 16 }, header: { padding: "14px 16px" } }}
    >
      {show ? <ReviewDrawerContent mode="task" taskId={taskId} userName="Task" /> : null}
    </Drawer>
  );
}

export function ProfileReviewsDrawer({
  show,
  onHide,
  role,
  userId,
  userName,
  title,
}) {
  const drawerTitle =
    title || `${role === "helper" ? "Helper" : "Requester"} Reviews`;
  const [fullWidth, setFullWidth] = useState(false);

  return (
    <Drawer
      open={show}
      onClose={onHide}
      placement="right"
      width={fullWidth ? "100vw" : 920}
      zIndex={2300}
      destroyOnClose
      title={<span style={{ fontSize: 13, fontWeight: 600 }}>{drawerTitle}</span>}
      extra={
        <DrawerExpandButton
          fullWidth={fullWidth}
          onToggle={() => setFullWidth((value) => !value)}
        />
      }
      styles={{ body: { background: "#f8fafc", padding: 16 }, header: { padding: "14px 16px" } }}
    >
      {show ? (
        <ReviewDrawerContent
          mode={role}
          requesterUserId={role === "requester" ? userId : undefined}
          helperUserId={role === "helper" ? userId : undefined}
          userName={userName}
        />
      ) : null}
    </Drawer>
  );
}

const getMapEmbedUrl = (latitude, longitude) => {
  if (!hasValue(latitude) || !hasValue(longitude)) return null;

  const lat = Number(latitude);
  const lng = Number(longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

  const delta = 0.01;
  const bbox = [lng - delta, lat - delta, lng + delta, lat + delta].join("%2C");

  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
};

export function ProgressPingsDrawer({ show, onHide, task, formatDate }) {
  const [fullWidth, setFullWidth] = useState(false);
  const pingCards = useMemo(() => {
    if (!task) return [];

    return [
      { key: "accepted", title: "Accepted Ping Location", ping: task.acceptedPingLocation },
      { key: "arrived", title: "Arrived Ping Location", ping: task.arrivedPingLocation },
      { key: "start", title: "Start Ping Location", ping: task.startPingLocation },
      {
        key: "navigation",
        title: "Start Navigation Ping Location",
        ping: task.startNavigationPingLocation,
      },
      { key: "completed", title: "Completed Ping Location", ping: task.completedPingLocation },
    ].filter((item) => item.ping != null);
  }, [task]);

  const renderMapPreview = (title, latitude, longitude, onOpenMap) => {
    const mapsUrl = getMapEmbedUrl(latitude, longitude);
    if (!mapsUrl) return null;

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpenMap(title, latitude, longitude)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpenMap(title, latitude, longitude);
          }
        }}
        className="position-relative rounded-4 overflow-hidden"
        style={{ height: 120, cursor: "pointer", background: "#f8f9fa" }}
      >
        <iframe
          title={`${title} preview`}
          src={mapsUrl}
          width="100%"
          height="100%"
          style={{ border: 0, pointerEvents: "none" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-end"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.22) 100%)",
          }}
        >
          <div className="w-100 px-3 py-2 d-flex justify-content-between align-items-center">
            <span className="badge text-bg-light text-dark fw-semibold">
              Tap to expand
            </span>
            <FontAwesomeIcon icon="location-dot" className="text-white" />
          </div>
        </div>
      </div>
    );
  };

  const [mapModal, setMapModal] = useState({
    open: false,
    title: "",
    latitude: null,
    longitude: null,
  });

  const openMapModal = (title, latitude, longitude) => {
    setMapModal({ open: true, title: title || "Map", latitude, longitude });
  };

  const closeMapModal = () => {
    setMapModal((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Drawer
        open={show}
        onClose={onHide}
        placement="right"
        width={fullWidth ? "100vw" : 1100}
        zIndex={2300}
        destroyOnClose
        title={<span style={{ fontSize: 13, fontWeight: 600 }}>Progress & Pings</span>}
        extra={
          <DrawerExpandButton
            fullWidth={fullWidth}
            onToggle={() => setFullWidth((value) => !value)}
          />
        }
        styles={{ body: { background: "#f8fafc", padding: 16 }, header: { padding: "14px 16px" } }}
      >
          {!task ? (
            <Alert variant="warning">No task available for progress details.</Alert>
          ) : (
            <>
              <Row className="g-3">
                <Col xs={12}>
                  <Card className="p-3 h-100 shadow-sm border-0 rounded-4">
                    <h6 className="fw-bold mb-2">
                      <FontAwesomeIcon icon="route" className="me-2" />
                      Ping Timeline
                    </h6>
                    <p className="text-muted text-sm mb-3">
                      Coordinates and location timestamps are grouped here so the main task page stays uncluttered.
                    </p>

                    {pingCards.length > 0 ? (
                      <div className="d-grid gap-3">
                        {pingCards.map(({ key, title, ping }) => {
                          const hasCoordinates =
                            hasValue(ping?.latitude) && hasValue(ping?.longitude);

                          return (
                            <Card key={key} className="p-3 shadow-sm border-0 rounded-4">
                              <div className="d-flex align-items-start justify-content-between gap-2">
                                <h6 className="fw-bold mb-0">{title}</h6>
                              </div>
                              <Row className="g-3 mt-2">
                                {hasCoordinates && (
                                  <Col xs={12}>
                                    <div>
                                      <div className="text-xxs text-muted fw-semibold mb-1">
                                        Coordinates
                                      </div>
                                      {renderMapPreview(
                                        title,
                                        ping.latitude,
                                        ping.longitude,
                                        openMapModal,
                                      )}
                                      <div className="mt-2 fw-semibold text-sm text-muted">
                                        {ping.latitude}, {ping.longitude}
                                      </div>
                                    </div>
                                  </Col>
                                )}
                                {hasValue(ping?.source) && (
                                  <Col xs={12} md={6}>
                                    <div className="text-xxs text-muted fw-semibold mb-1">
                                      Source
                                    </div>
                                    <div className="fw-semibold text-sm">{ping.source}</div>
                                  </Col>
                                )}
                                {hasValue(ping?.clientId) && (
                                  <Col xs={12} md={6}>
                                    <div className="text-xxs text-muted fw-semibold mb-1">
                                      Client ID
                                    </div>
                                    <div className="fw-semibold text-sm">
                                      <CopyableText value={ping.clientId} />
                                    </div>
                                  </Col>
                                )}
                                {hasValue(ping?.actorId) && (
                                  <Col xs={12} md={6}>
                                    <div className="text-xxs text-muted fw-semibold mb-1">
                                      Actor ID
                                    </div>
                                    <div className="fw-semibold text-sm">
                                      <CopyableText value={ping.actorId} />
                                    </div>
                                  </Col>
                                )}
                                {hasValue(ping?.pingAt) && (
                                  <Col xs={12} md={6}>
                                    <div className="text-xxs text-muted fw-semibold mb-1">
                                      Ping At
                                    </div>
                                    <div className="fw-semibold text-sm">
                                      {formatDate(ping.pingAt)}
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-muted">
                        No ping records were captured for this task.
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            </>
          )}
      </Drawer>

      <Modal show={mapModal.open} onHide={closeMapModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{mapModal.title || "Map"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div style={{ width: "100%", height: "70vh", background: "#f8f9fa" }}>
            {getMapEmbedUrl(mapModal.latitude, mapModal.longitude) ? (
              <iframe
                title={mapModal.title || "Map"}
                src={getMapEmbedUrl(mapModal.latitude, mapModal.longitude)}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                No map available
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

function renderCompensationValue(value, currency) {
  if (!hasValue(value)) return null;
  if (typeof value === "number") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  }
  return value;
}

export function InterestedHelpersCard({ interestedHelpers = [] }) {
  const navigate = useNavigate();

  if (!Array.isArray(interestedHelpers) || interestedHelpers.length === 0) return null;

  return (
    <Card className="mb-3 p-3 pb-2 custom-shadow border-0 rounded-4">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <h6 className="fw-bold mb-0">
          <FontAwesomeIcon icon="users" className="me-2" />
          Interested Helpers
        </h6>
        <Tag color="blue">{interestedHelpers.length}</Tag>
      </div>

      <div className="mt-3 d-grid gap-3">
        {interestedHelpers.map((entry, index) => {
          const helperName = getDisplayName(entry.helper);
          const partyLabel = entry.interestedPartyName || helperName || `Helper ${index + 1}`;
          const statusColor =
            entry.status === "ACTIVE"
              ? "green"
              : entry.status === "PENDING"
                ? "gold"
                : entry.status === "CANCELLED"
                  ? "red"
                  : "blue";

          return (
            <Card key={entry.interestedPartyId || `${partyLabel}-${index}`} className="custom-shadow border rounded-4">
              <Card.Body className="custom-shadow">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div>
                    <div className="fw-bold">{partyLabel}</div>
                    <div className="text-muted text-xs">
                      {entry.interestedPartyType || "Helper interest"}
                    </div>
                  </div>
                  <Tag color={statusColor}>{formatStatus(entry.status)}</Tag>
                </div>

                <Row className="g-3 mt-2">
                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">Party ID</div>
                    <div className="fw-semibold text-sm">
                      {hasValue(entry.interestedPartyId) ? (
                        <CopyableText value={entry.interestedPartyId} />
                      ) : (
                        "—"
                      )}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">Party Name</div>
                    <div className="fw-semibold text-sm">{entry.interestedPartyName || "—"}</div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">Party Type</div>
                    <div className="fw-semibold text-sm">{entry.interestedPartyType || "—"}</div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">Party Status</div>
                    <div className="fw-semibold text-sm">{entry.interestedPartyStatus || "—"}</div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">Helper</div>
                    <div className="fw-semibold text-sm">
                      {helperName}
                      {entry.helper?.email ? (
                        <div className="text-muted text-xs">{entry.helper.email}</div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Helper Organization
                    </div>
                    <div className="fw-semibold text-sm">
                      {entry.helperBusinessEntity?.name || "—"}
                      {entry.helperBusinessEntity?.status ? (
                        <div className="text-muted text-xs">
                          {entry.helperBusinessEntity.status}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Helper Organization ID
                    </div>
                    <div className="fw-semibold text-sm">
                      {hasValue(entry.helperBusinessEntityId) ? (
                        <CopyableText value={entry.helperBusinessEntityId} />
                      ) : (
                        "—"
                      )}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Helper Profession Profile ID
                    </div>
                    <div className="fw-semibold text-sm">
                      {hasValue(entry.helperProfessionProfileId) ? (
                        <CopyableText value={entry.helperProfessionProfileId} />
                      ) : (
                        "—"
                      )}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Helper Background Check ID
                    </div>
                    <div className="fw-semibold text-sm">
                      {hasValue(entry.helperBackgroundCheckId) ? (
                        <Button
                          variant="link"
                          className="p-0 text-decoration-none align-baseline"
                          style={{
                            minHeight: 0,
                            lineHeight: 1.3,
                            fontSize: "inherit",
                            fontWeight: 600,
                          }}
                          onClick={() =>
                            navigate(
                              paths.helperProfessionBackgroundCheckDetails(
                                entry.helperBackgroundCheckId,
                              ),
                            )
                          }
                        >
                          {entry.helperBackgroundCheckId}
                        </Button>
                      ) : (
                        "—"
                      )}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">Interested At</div>
                    <div className="fw-semibold text-sm">
                      {entry.interestedAt ? new Date(entry.interestedAt).toLocaleString() : "—"}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">Cancelled At</div>
                    <div className="fw-semibold text-sm">
                      {entry.cancelledAt ? new Date(entry.cancelledAt).toLocaleString() : "—"}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Missing CareGiver Requirements
                    </div>
                    <div className="fw-semibold text-sm">
                      {entry.missingCareGiverRequirements
                        ? entry.missingCareGiverRequirements
                        : "—"}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Has Missing CareGiver Requirements
                    </div>
                    <div className="fw-semibold text-sm">
                      {entry.hasMissingCareGiverRequirements === null ||
                      entry.hasMissingCareGiverRequirements === undefined
                        ? "—"
                        : String(entry.hasMissingCareGiverRequirements)}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Missing Helper Sector Requirements
                    </div>
                    <div className="fw-semibold text-sm">
                      {entry.missingHelperSectorRequirements
                        ? entry.missingHelperSectorRequirements
                        : "—"}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <div className="text-xxs text-muted fw-semibold mb-1">
                      Has Missing Helper Sector Requirements
                    </div>
                    <div className="fw-semibold text-sm">
                      {entry.hasMissingHelperSectorRequirements === null ||
                      entry.hasMissingHelperSectorRequirements === undefined
                        ? "—"
                        : String(entry.hasMissingHelperSectorRequirements)}
                    </div>
                  </Col>
                </Row>

                {(entry.helperBusinessEntity || entry.helper) && (
                  <div className="mt-3 pt-3 border-top">
                    <Row className="g-3">
                      {entry.helperBusinessEntity && (
                        <Col xs={12} md={6}>
                          <div className="text-xxs text-muted fw-semibold mb-1">
                            Helper Organization Details
                          </div>
                          <div className="fw-semibold text-sm">
                            {entry.helperBusinessEntity.name}
                            <div className="text-muted text-xs">
                              {entry.helperBusinessEntity.type}{" "}
                              {entry.helperBusinessEntity.email
                                ? `• ${entry.helperBusinessEntity.email}`
                                : ""}
                            </div>
                          </div>
                        </Col>
                      )}
                      {entry.helper && (
                        <Col xs={12} md={6}>
                          <div className="text-xxs text-muted fw-semibold mb-1">
                            Helper Details
                          </div>
                          <div className="fw-semibold text-sm">
                            {getDisplayName(entry.helper)}
                            <div className="text-muted text-xs">
                              {entry.helper.email || "No helper email"}
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    </Card>
  );
}

export function CompensationCard({ compensation }) {
  if (!compensation) return null;

  return (
    <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
      <h6 className="mt-3 px-3 fw-bold">
        <FontAwesomeIcon icon="money-bill" className="me-2" />
        Compensation
      </h6>
      <div className="px-3 mt-2">
        <Row className="g-3">
          <Col xs={12} md={6}>
            <div>
              <div className="text-xxs text-muted fw-semibold mb-1">Pay Type</div>
              <div className="fw-semibold text-sm">{compensation.payType || "—"}</div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div>
              <div className="text-xxs text-muted fw-semibold mb-1">Currency</div>
              <div className="fw-semibold text-sm">{compensation.currency || "—"}</div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div>
              <div className="text-xxs text-muted fw-semibold mb-1">Pay Rate</div>
              <div className="fw-semibold text-sm">
                {renderCompensationValue(compensation.payRate, compensation.currency)}
              </div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div>
              <div className="text-xxs text-muted fw-semibold mb-1">Estimated Total</div>
              <div className="fw-semibold text-sm">
                {renderCompensationValue(compensation.estimatedTotal, compensation.currency)}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </Card>
  );
}
